import { useCallback, useState } from 'react';
import { Modal, message } from 'antd';
import {
  JobFitProgress,
  JobFitResult,
  JobService,
} from '../../services/job.service';
import { JOB_FIT_PROGRESS_PERCENT, JobFitPhase } from './jobFit.shared';
import {
  KofiRequiredError,
  ToolGateStatus,
} from '../../../support/kofi.service';
import { useToolGate } from '../../../support/useToolGate';

export function useJobFitFlow(jobId: string) {
  const [phase, setPhase] = useState<JobFitPhase>('idle');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [progressMessage, setProgressMessage] = useState(
    'Uploading your resume…'
  );
  const [progressPercent, setProgressPercent] = useState(0);
  const [fitResult, setFitResult] = useState<JobFitResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { status, kofiRequired, setKofiRequired, refresh, onRedeemed } =
    useToolGate();

  const handleProgress = useCallback((progress: JobFitProgress) => {
    setProgressMessage(progress.message);
    setProgressPercent(JOB_FIT_PROGRESS_PERCENT[progress.status] ?? 30);
  }, []);

  const reset = useCallback(() => {
    setPhase('idle');
    setSelectedFile(null);
    setProgressMessage('Uploading your resume…');
    setProgressPercent(0);
    setFitResult(null);
    setErrorMessage(null);
    setKofiRequired(null);
  }, [setKofiRequired]);

  const runFitCheck = useCallback(
    async (file: File) => {
      setPhase('working');
      setProgressPercent(10);
      setProgressMessage('Uploading your resume…');
      setErrorMessage(null);
      setKofiRequired(null);
      setFitResult(null);

      try {
        const result = await JobService.runJobFit(jobId, file, handleProgress);
        if (result.fit) {
          setFitResult(result.fit);
          setPhase('completed');
          setProgressPercent(100);
          void refresh();
        } else {
          throw new Error(
            result.errorMessage ?? 'Fit check did not return a result.'
          );
        }
      } catch (error: unknown) {
        if (error instanceof KofiRequiredError) {
          setKofiRequired(error);
          setPhase('payment');
          setProgressPercent(0);
          return;
        }
        const msg =
          error instanceof Error
            ? error.message
            : 'We could not analyze your resume. Please try again.';
        setErrorMessage(msg);
        setPhase('failed');
        setProgressPercent(0);
      }
    },
    [handleProgress, jobId, refresh, setKofiRequired]
  );

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const requestCloseModal = useCallback(() => {
    if (phase === 'working') {
      Modal.confirm({
        title: 'Analysis in progress',
        content: 'Your resume is still being analyzed. Close anyway?',
        okText: 'Close',
        cancelText: 'Stay',
        onOk: closeModal,
      });
      return;
    }
    closeModal();
  }, [closeModal, phase]);

  const selectFile = useCallback(
    (file: File | null) => {
      setSelectedFile(file);
      if (phase === 'failed') {
        setErrorMessage(null);
        setPhase('idle');
      }
    },
    [phase]
  );

  const analyzeSelectedFile = useCallback(() => {
    if (!selectedFile) {
      message.warning('Choose a PDF or DOCX resume first.');
      return;
    }
    void runFitCheck(selectedFile);
  }, [runFitCheck, selectedFile]);

  const handleRedeemed = useCallback(
    (next: ToolGateStatus) => {
      onRedeemed(next);
      if (selectedFile) {
        void runFitCheck(selectedFile);
      }
    },
    [onRedeemed, runFitCheck, selectedFile]
  );

  const checkAgain = useCallback(() => {
    reset();
  }, [reset]);

  return {
    phase,
    modalOpen,
    selectedFile,
    progressMessage,
    progressPercent,
    fitResult,
    errorMessage,
    gateStatus: status,
    kofiRequired,
    openModal,
    closeModal,
    requestCloseModal,
    selectFile,
    analyzeSelectedFile,
    handleRedeemed,
    checkAgain,
    reset,
  };
}

export type JobFitFlow = ReturnType<typeof useJobFitFlow>;
